/**
 * When two exercise names are the same name, and when a search term is found in one.
 *
 * The browser's face of the backend's `ExerciseNames.Normalize` (exercise-library R4, ADR
 * 2026-10-10 "exercise aliases and private tags"). The rule lives on both sides of the wire — the
 * server searches for the phone, this app searches its own copy of the catalogue — so the two are
 * pinned by the parity fixture the backend emits (`exerciseNameParity.test.ts`), never by hoping
 * two hand-kept implementations agree.
 *
 * ── The rule ────────────────────────────────────────────────────────────────────────────────
 * Lower case; `ł` read as `l`, because it has no Unicode decomposition and survives NFD untouched
 * (without it "zolnierskie" never reaches "żołnierskie"); diacritics removed through NFD and the
 * non-spacing marks it leaves; and every run of anything that is not a letter or a digit collapsed
 * into one space, trimmed at both ends. So "T-bar", "t bar" and "T BAR" are one name, and a term
 * that is only punctuation normalises to nothing — which is no term at all.
 *
 * The `ł` is replaced BEFORE decomposition, as the backend does it. The order matters only for
 * the uppercase `Ł`, which lower-cases to `ł` first and is then caught by the same replace.
 *
 * No React, no DOM, no i18next: this is `model/`.
 */

const NON_SPACING_MARK = /\p{Mn}/gu;
/** Letters of every script and decimal digits — .NET's `char.IsLetterOrDigit`. */
const NOT_LETTER_OR_DIGIT = /[^\p{L}\p{Nd}]+/gu;

/** A name with case, diacritics and punctuation set aside. Two names are one when these agree. */
export function normaliseExerciseName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/ł/g, 'l')
    .normalize('NFD')
    .replace(NON_SPACING_MARK, '')
    .replace(NOT_LETTER_OR_DIGIT, ' ')
    .trim();
}

/**
 * How many distinct words of a term take part in a search — the server's own cap (R4). A longer
 * term is cut to its first eight distinct words, so a ninth word narrows nothing on either side of
 * the wire, and the web and the phone find the same exercises for it.
 */
export const MAX_SEARCH_WORDS = 8;

/**
 * The words of a search term: normalised, each counted once in the order first typed, and at most
 * `MAX_SEARCH_WORDS` of them. Empty for a term that normalises to nothing.
 */
export function searchWords(term: string): readonly string[] {
  const normalised = normaliseExerciseName(term);
  if (normalised === '') return [];
  return [...new Set(normalised.split(' '))].slice(0, MAX_SEARCH_WORDS);
}

/**
 * Does the term land in ONE of these names?
 *
 * Every word must occur, as a substring, inside the same name: "sztanga przysiad" finds
 * "Przysiad ze sztangą", but an exercise whose primary name holds one word and an alias the other
 * is not a name anybody typed, and letting it through would widen every two-word search into two
 * unrelated half-matches. No words means no term, and every name matches it.
 */
export function wordsMatchOneName(words: readonly string[], names: readonly string[]): boolean {
  if (words.length === 0) return true;
  return names.some((name) => {
    const normalised = normaliseExerciseName(name);
    return words.every((word) => normalised.includes(word));
  });
}
